// eslint-disable-next-line no-unused-vars
import React, { memo, useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import debounce from 'lodash/debounce';
import uniqueId from 'lodash/uniqueId';

import OrderQuickAddComponent from 'Components/Order/OrderQuickAdd';
import { API_PRODUCTS_SEARCH } from 'Constants';
import useCart from 'Hooks/useCart';
import useUser from 'Hooks/useUser';
import fetch from 'Utils/fetch';
import useToastNotifications from 'Hooks/useToastNotifications';
import useAnalytics from 'Hooks/useAnalytics';

const OrderQuickAdd = memo(() => {
  const toast = useToastNotifications();
  const { t } = useTranslation();
  const { data: user } = useUser();
  const { data: cart, updateProducts } = useCart();
  const analytics = useAnalytics();
  const [errors, setErrors] = useState([]);
  const [inputProducts, setMatches] = useState([]);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [addToCartLoading, setAddToCartLoading] = useState(false);
  const [canAddToCart, setCanAddToCart] = useState(false);
  const regex = useRef(new RegExp('([A-Za-z0-9-._+]+)', 'gm')); // eslint-disable-line
  const [input, setInput] = useState('');
  const [shouldUpdateInput, setShouldUpdateInput] = useState(false);

  const handleOnCancel = () => {
    analytics.track('order_quick_add_cancel');
    setErrors([]);
    setSelectedProduct(null);
  };

  const handleOnSelect = replacement => {
    analytics.track('order_quick_add_select', { product: replacement.code });
    setSelectedProduct(replacement);
  };

  const handleOnConfirmSelection = original => {
    analytics.track('order_quick_add_confirm_selection', { product: selectedProduct.code });
    const existingIdx = inputProducts.findIndex(i => i[0] === selectedProduct.code);
    if (existingIdx !== -1) {
      setMatches(prevMatches => {
        const productToRemoveIdx = inputProducts.findIndex(p => p[0] === original);
        const productToRemove = inputProducts[productToRemoveIdx];
        prevMatches.splice(existingIdx, 1, [
          selectedProduct.code,
          prevMatches[existingIdx][1] * 1 + productToRemove[1] * 1
        ]);
        prevMatches.splice(productToRemoveIdx, 1);
        return [...prevMatches];
      });
    } else {
      setMatches(prevMatches => prevMatches.reduce((acc, prod) => {
        const [code, qty] = prod;
        if (code === original) {
          acc.push([selectedProduct.code, qty]);
        } else {
          acc.push(prod);
        }
        return acc;
      }, []));
    }
    setShouldUpdateInput(true);
    setErrors(prevErrors => prevErrors.reduce((acc, error) => {
      if (
        error.type === 'notFound'
        && error.productCode === original
      ) {
        return acc;
      }
      acc.push(error);
      return acc;
    }, []));
    setSelectedProduct(null);
  };

  const handleOnRemoveItem = original => {
    analytics.track('order_quick_add_remove_item', { product: original });
    setMatches(prevMatches => prevMatches.reduce((acc, prod) => {
      const [code] = prod;
      if (code !== original) {
        acc.push(prod);
      }
      return acc;
    }, []));
    setShouldUpdateInput(true);
    setErrors(prevErrors => prevErrors.reduce((acc, error) => {
      if (
        error.productCode === original
        && (
          error.type === 'notAvailable'
          || error.type === 'notFound'
        )
      ) {
        return acc;
      }
      acc.push(error);
      return acc;
    }, []));
  };

  const handleOnChangeQuantity = (original, found) => {
    analytics.track('order_quick_add_change_quantity', { product: original, quantity: found.minQuantity });
    setMatches(prevMatches => prevMatches.reduce((acc, prod) => {
      const [code, qty] = prod;
      if (code === original) {
        const quantity = Math.min(
          Math.max(1, Math.floor(qty / found.minQuantity)) * found.minQuantity,
          found.stockValue
        );
        acc.push([code, quantity]);
      } else {
        acc.push(prod);
      }
      return acc;
    }, []));
    setShouldUpdateInput(true);
    setErrors(prevErrors => prevErrors.reduce((acc, error) => {
      if (
        error.type === 'quantity'
        && error.productCode === original
      ) {
        return acc;
      }
      acc.push(error);
      return acc;
    }, []));
  };

  const handleOnAddToCart = async () => {
    analytics.track('order_quick_add_add_to_cart', { products: inputProducts.map(p => p[0]).join(', ') });
    setAddToCartLoading(true);
    setErrors([]);
    setShouldUpdateInput(true);
    const responses = await Promise.allSettled(
      inputProducts.reduce((acc, productPair) => {
        const [productCode] = productPair;
        acc.push(
          fetch(
            `${ API_PRODUCTS_SEARCH }?search=${ productCode }&only_available=1&currency=${ user?.defaultCurrency }&vat=${ user?.withVat }`,
            { cacheKey: uniqueId() }
          )
        );
        return acc;
      }, [])
    );
    let hasErrors = false;
    let productsToAdd = [];
    responses.forEach((response, idx) => {
      const [code, quantity] = inputProducts[idx];
      const qty = quantity * 1;
      const { products = [] } = response?.value || {};
      if (response.status !== 'fulfilled') {
        hasErrors = true;
        setErrors(prevErrors => {
          prevErrors.push({
            type: 'notAvailable',
            productCode: code,
            message: response.value.errors?.at(0)?.message || response.value.error.message || t('common.searchFailed')
          });
        });
      } else if (!products?.length) {
        hasErrors = true;
        setErrors(prevErrors => {
          prevErrors.push({
            type: 'notAvailable',
            productCode: code,
            message: t('orders.quickOrderNotAvailable', { product: code })
          });
          return [...prevErrors];
        });
      } else if (products.some(p => p.hasPrice === false)) {
        hasErrors = true;
        setErrors(prevErrors => {
          prevErrors.push({
            type: 'notAvailable',
            productCode: code,
            message: t('orders.quickOrderSupplierProduct', { product: code })
          });
          return [...prevErrors];
        });
      } else {
        const foundProduct = products.find(prd => prd.code === code);
        if (!foundProduct) {
          hasErrors = true;
          setErrors(prevErrors => {
            prevErrors.push({
              type: 'notFound',
              message: t('orders.quickOrderNotFound', { product: code }),
              productCode: code,
              suggestedProducts: products
            });
            return [...prevErrors];
          });
        } else if (foundProduct.minQuantity > foundProduct.stockValue) {
          hasErrors = true;
          setErrors(prevErrors => {
            prevErrors.push({
              type: 'notAvailable',
              productCode: code,
              message: t('orders.quickOrderNotEnoughStock', { product: code })
            });
            return [...prevErrors];
          });
        } else if (qty > foundProduct.stockValue) {
          hasErrors = true;
          setErrors(prevErrors => {
            prevErrors.push({
              type: 'quantity',
              productCode: code,
              foundProduct,
              message: t('orders.quickOrderAvailableQty', { product: code, stockValue: foundProduct.stockValue })
            });
            return [...prevErrors];
          });
        } else if (qty % foundProduct.minQuantity !== 0) {
          hasErrors = true;
          setErrors(prevErrors => {
            prevErrors.push({
              type: 'quantity',
              productCode: code,
              foundProduct,
              message: t('orders.quickOrderMinQty', { product: code, minQty: foundProduct.minQuantity })
            });
            return [...prevErrors];
          });
        } else {
          productsToAdd.push({
            ...foundProduct,
            quantity: qty
          });
        }
      }
    });

    if (!hasErrors) {
      try {
        productsToAdd = productsToAdd.reduce((acc, prod) => {
          const existing = (cart?.items || []).find(p => p.productId === prod.id && !p.promotion);
          if (existing) {
            setErrors(prevErrors => {
              prevErrors.push({
                type: 'err',
                productCode: prod.code,
                message: t('orders.quickOrderAlreadyInCart', { product: prod.code })
              });
              return [...prevErrors];
            });
          } else {
            acc.push({
              ...prod,
              productId: prod.id,
              quantity: prod.quantity || 1
            });
          }
          // const stockValue = 'stockValue' in existing
          //   ? existing.stockValue
          //   : prod.stockValue;
          // if ((existing?.quantity || 0) + prod.quantity > stockValue) {
          //   toast.addNotification({
          //     type: 'warning',
          //     title: t('products.insufficientStock'),
          //     message: prod.code,
          //     timeout: 7500
          //   });
          //   return acc;
          // }
          // acc.push({
          //   ...existing,
          //   ...prod,
          //   quantity: Math.min(
          //     (existing?.quantity || 0) + prod.quantity,
          //     stockValue
          //   )
          // });
          return acc;
        }, []);
        if (productsToAdd.length) {
          await updateProducts(productsToAdd);
          toast.addNotification({
            title: t('cart.successfullyAddedProductsToCart')
          });
        }
        setInput('');
        setMatches([]);
      } catch (err) {
        toast.addNotification({
          type: 'error',
          title: t('cart.failedAddingProductsToCart'),
          message: err?.info?.errors?.at(0)?.message
        });
        console.error(err);
      }
    }

    setAddToCartLoading(false);
  };

  const groupResults = (matches, acc = []) => {
    const items = [...matches];
    let group = [];

    items.forEach((value, idx) => {
      // eslint-disable-next-line no-restricted-globals
      const isNumber = !isNaN(Number(value));
      const typeofValue = isNumber ? 'number' : 'string';
      const nextValue = items[idx + 1];
      const foundIdx = acc.findIndex(i => i[0] === value);

      if (foundIdx !== -1) {
        acc[foundIdx][1] = parseInt(acc[foundIdx][1], 10) + parseInt(matches[idx + 1], 10);
        return; // continue
      }

      group.push(value);

      // eslint-disable-next-line no-restricted-globals
      const isNextNaN = nextValue === undefined || isNaN(Number(nextValue));
      const isEvenIndex = (idx + 1) % 2 === 0;

      if (
        (typeofValue === 'string' && isNextNaN)
        || (isEvenIndex && group.length === 2)
        || (isEvenIndex && typeofValue === 'string')
      ) {
        acc.push(group);
        group = [];
      } else if (isEvenIndex) {
        group = [];
      }
    });
    return acc;
  };

  const debouncedChange = useCallback(
    debounce(value => {
      const matches = (value || '').match(regex.current);
      if (!matches) return;
      const groupedMatches = groupResults(matches);
      if (groupedMatches.length === 0) return;
      let canAdd = groupedMatches.length > 0;
      setErrors([]);
      const existingProducts = (cart?.items || []).filter(p => groupedMatches.some(([id]) => id === p.code && !p.promotion));
      if (existingProducts.length > 0) {
        existingProducts.forEach(prod => {
          setErrors(prevErrors => {
            prevErrors.push({
              type: 'err',
              productCode: prod.code,
              message: t('orders.quickOrderAlreadyInCart', { product: prod.code })
            });
            return [...prevErrors];
          });
        });
        canAdd = false;
      }
      groupedMatches.forEach(group => {
        const [product, qty] = group;
        const quantity = parseInt(qty, 10);
        if (product?.length < 3) {
          setErrors(prevErrors => {
            prevErrors.push({
              type: 'err',
              productCode: product,
              message: t('orders.quickOrderInvalidText')
            });
            return [...prevErrors];
          });
        }
        if (
          product
          && (
            qty === undefined
            // eslint-disable-next-line no-restricted-globals
            || isNaN(quantity)
          )
        ) {
          setErrors(prevErrors => {
            prevErrors.push({
              type: 'err',
              productCode: product,
              /* eslint-disable */
              message: t('orders.quickOrderNoQty', { product }),
              /* eslint-enable */
            });
            return [...prevErrors];
          });
          canAdd = false;
        } else if (
          quantity > 100
          || quantity <= 0
        ) {
          setErrors(prevErrors => {
            prevErrors.push({
              type: 'err',
              productCode: product,
              message: t('orders.quickOrderInvalidQty', { product })
            });
            return [...prevErrors];
          });
          canAdd = false;
        }
      });
      if (canAdd) {
        setMatches(groupedMatches);
      }
      setCanAddToCart(canAdd);
    }, 250, { leading: false, trailing: true }),
    [cart]
  );

  const handleOnChange = evt => {
    analytics.track('order_quick_add_input_change', { value: evt.target.value });
    const { value } = evt.target;
    setInput(value);
    if (!value && errors) {
      setErrors([]);
    }
    setShouldUpdateInput(false);
    debouncedChange(value);
  };

  useEffect(() => () => {
    debouncedChange.cancel();
  }, []);

  useEffect(() => {
    if (shouldUpdateInput) {
      setInput(inputProducts.reduce((acc, [code, qty]) => `${acc}${code} ${qty}\n`, ''));
    }
  }, [
    shouldUpdateInput,
    inputProducts
  ]);

  return (
    <OrderQuickAddComponent
      onInputChange={ handleOnChange }
      onRemoveItem={ handleOnRemoveItem }
      inputProducts={ inputProducts }
      addToCartLoading={ addToCartLoading }
      addToCartEnabled={ canAddToCart }
      onChangeQuantity={ handleOnChangeQuantity }
      errors={ errors }
      selectedProduct={ selectedProduct }
      onCancel={ handleOnCancel }
      onSelect={ handleOnSelect }
      onConfirmSelect={ handleOnConfirmSelection }
      onAddToCart={ handleOnAddToCart }
      input={ input }
    />
  );
});
OrderQuickAdd.displayName = 'OrderQuickAddContainer';

export default OrderQuickAdd;
